// src/pages/PressPage.jsx
import React, { Fragment } from "react";
import { Helmet } from "react-helmet";
import Header from "../../component/layout/header";
import Footer from "../../component/layout/footer";
import PageHeader from "../../component/layout/pageheader";

const PressPage = () => (
  <>
    {/* ────── <SEO / Social preview> ─────────────────────────────── */}
    <Helmet prioritizeSeoTags>
      <title>Press | Glitch — AI Marketing for Indie Games</title>
      <meta
        name="description"
        content="Latest Glitch news, press releases, brand assets, and media contact information for journalists covering the indie‑game industry."
      />
      {/* Social preview */}
      <meta property="og:title" content="Glitch Press Room" />
      <meta
        property="og:description"
        content="The first AI marketing assistant built for indie game developers."
      />
      <meta property="og:image" content="/assets/og/press-room.jpg" />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
    </Helmet>

    {/* ────── <Page shell> ───────────────────────────────────────── */}
    <Fragment>
      <Header />
      <PageHeader title="Press" curPage="Press" />

      {/* ────── <Hero> ───────────────────────────────────────────── */}
      <section className="py-5 bg-dark text-light">
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <h1 className="display-5 fw-bold mb-3">
                🚀 Glitch Launches the First AI Marketing Assistant Built for
                Indie Game Developers
              </h1>
              <p className="lead">
                Helping small teams turn wishlists into revenue — without ads,
                agencies, or publishers
              </p>
              <p className="text-uppercase  mt-3">
                April 19 2025 · New York, NY
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ────── <Body copy> ──────────────────────────────────────── */}
      <section className="about-section py-5">
        <div className="container">
          <article className="mx-auto" style={{ maxWidth: "850px" }}>
            {/* Overview */}
            <p className="fs-5">
              <strong>Glitch</strong>, the AI‑powered marketing platform for
              indie game developers, is redefining how games find their
              audience — and stay profitable. In an industry where&nbsp;
              <em>9 out of 10</em> indie titles fail to make over $10k, Glitch gives
              small teams a fighting chance with an automated assistant that
              builds audiences, grows wishlists, and drives sales — all without
              paid ads or traditional publishers.
            </p>

            <blockquote className="blockquote my-4 px-4 py-3 bg-dark text-light rounded-2 border-start border-4 border-primary">
              <p className="mb-0 fs-5">
                “Marketing is the #1 reason good games fail,” says&nbsp;
                <strong>Devin Dixon</strong>, founder & CEO of Glitch. “Most
                developers don’t have the time, money, or the training to market
                effectively. We built Glitch so indies can compete without
                spending $50K on an agency.”
              </p>
            </blockquote>

            {/* Built to Automate */}
            <h2 className="h4 fw-bold mt-5 mb-3">
              💡 Built to Automate What Actually Works
            </h2>
            <ul className="list-unstyled ps-lg-3">
              <li className="mb-2">• Achieved <span className="fw-semibold">500&nbsp;wishlists per day</span> post‑demo</li>
              <li className="mb-2">• Cut CPW from <span className="fw-semibold">$3.00</span> to <span className="fw-semibold">$0.19</span></li>
              <li className="mb-2">• Ranked in the <span className="fw-semibold">top 3 %</span> of Steam launches using only organic marketing</li>
              <li className="mb-2">• Games are experiencing a <span className="fw-semibold">32% wishlist</span> to sales conversion, higher than less than 10% normally seen</li>
            </ul>

            {/* How it works */}
            <h2 className="h4 fw-bold mt-5 mb-3">🛠 How It Works</h2>
            <div className="row gy-4">
              <div className="col-md-4">
                <div className="p-3 h-100 bg-dark text-light rounded-3">
                  <h3 className="h6 text-uppercase mb-2">
                    AI Content Engine
                  </h3>
                  <p className="small mb-0">
                    Auto‑generates social posts, dev‑logs, and influencer
                    outreach calendars.
                  </p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="p-3 h-100 bg-dark text-light rounded-3">
                  <h3 className="h6 text-uppercase mb-2">
                    Audience Builder
                  </h3>
                  <p className="small mb-0">
                    Tracks wishlist growth, retention, and engagement with smart
                    insights.
                  </p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="p-3 h-100 bg-dark text-light rounded-3">
                  <h3 className="h6 text-uppercase mb-2">
                    Cross‑Promotion Network
                  </h3>
                  <p className="small mb-0">
                    Lets devs amplify each other’s reach—just like AAA
                    publishers do.
                  </p>
                </div>
              </div>
            </div>

            {/* RevShare */}
            <h2 className="h4 fw-bold mt-5 mb-3">
              🔥 A New Model: Revenue Before Launch
            </h2>
            <p>
              Glitch is piloting <strong>RevShare Model</strong>—instead of
              demanding a 15× return, we partner early and earn revenue as the
              game grows. It’s a model designed to de‑risk launches, not punish
              success.
            </p>

            {/* Backing */}
            <h2 className="h4 fw-bold mt-5 mb-3">🕹 Backed by Leading Accelerators</h2>
            <p>
              Glitch is supported by&nbsp;
              <strong>HustleFund</strong>,&nbsp;
              <strong>Visible Hands</strong>, and&nbsp;
              <strong>Techstars Tulsa</strong>, with indie studios already
              seeing measurable success.
            </p>

            <blockquote className="blockquote my-4 px-4 py-3 bg-dark text-light rounded-2 border-start border-4 border-primary">
              <p className="mb-0 fs-5">
              "Glitch is doing for indie devs what HubSpot did for small businesses," says one early investor. "They're giving power back to creators who’ve been overlooked for too long."
              </p>
            </blockquote>

            {/* Media contact */}
            <hr className="text-secondary my-5" />
            <section>
              <h3 className="h5 fw-bold mb-3">Press & Media Contact</h3>
              <address className="small">
                Devin Dixon — Founder & CEO<br />
                <a href="mailto:press@glitch.fun" className="text-decoration-underline">
                  press@glitch.fun
                </a>
              </address>
           
            </section>
          </article>
        </div>
      </section>

      <Footer />
    </Fragment>
  </>
);

export default PressPage;
